import type { FormEvent } from 'react'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { Box, Typography } from '@mui/material'
import { useAuthContext } from 'AuthProvider'
import { InfoMessageDataList, OrderAlertDataList, SearchCondition } from 'components/parts/Top'
import { useInfoMessage } from 'hooks/useInfoMessage'
import { useOrderAlert } from 'hooks/useOrderAlert'
import { useSearchParamStore } from 'store/searchParamStore'
import { initDataListStatus } from 'types/types'
import { getLocalSearchParam } from 'utils/getLocalSearchParam'

import type { GetOrderAlertOrderAlertGetRequest } from 'api-client'
import type { DataListStatus } from 'types/types'

function Top() {
  const { loginUserInfo } = useAuthContext()

  const searchParamState = useSearchParamStore((state) => state.searchParamState)
  const setSearchParamState = useSearchParamStore((state) => state.setSearchParamState)

  const { infomessage, fetchInfomessage, isLoading: infoMessageIsLoading } = useInfoMessage()
  const { orderAlert, fetchOrderAlert, fetchAlertSummary, isLoading: orderAlertIsLoading, result } = useOrderAlert()

  // 自社の検索条件をローカルストレージから取得する
  const localSearchParam = getLocalSearchParam(searchParamState, loginUserInfo?.tenantId, loginUserInfo?.companyId)

  const localCenterIds = localSearchParam?.top.centerIds || []
  const localOrgSupplierIds = localSearchParam?.top.orgSupplierIds || []
  const localSort = localSearchParam?.top.sort || []
  const localPageNo = localSearchParam?.top.pageNo || 0
  const localLimit = localSearchParam?.top.limit || 100

  // クエリパラメータかローカルストレージに保存している検索条件を初期値に指定
  const initSearchParam: GetOrderAlertOrderAlertGetRequest = {
    // NOTE:centerIdは必須項目になっていてundefinedできないため、0を初期値としておく
    // SearchConditionコンポーネントの方で初期値をundefinedする対応を行う
    centerId: localCenterIds[0] ?? 0,
    centerIds: localCenterIds,
    orgSupplierIds: localOrgSupplierIds,
    sort: localSort,
    pageNo: localPageNo,
    limit: localLimit,
  }

  const [searchParam, setSearchParam] = useState<GetOrderAlertOrderAlertGetRequest>(initSearchParam)
  const [listStatus, setListStatus] = useState<DataListStatus>({
    ...initDataListStatus(),
  })
  const hasLoadedInitialOrderAlert = useRef(false)

  const fetchTopAlertData = useCallback(async (requestParam: GetOrderAlertOrderAlertGetRequest) => {
    await Promise.all([
      fetchOrderAlert(requestParam),
      fetchAlertSummary({
        centerIds: requestParam.centerIds,
        orgSupplierIds: requestParam.orgSupplierIds,
        enabled: false,
      }),
    ])
  }, [fetchOrderAlert, fetchAlertSummary])

  const onSearch = (requestParam: GetOrderAlertOrderAlertGetRequest, e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // 検索API実行
    fetchTopAlertData({
      ...requestParam,
      limit: listStatus.paginationModel.pageSize,
      pageNo: 0,
    }).catch(() => undefined)
    setSearchParam(requestParam)

    if (loginUserInfo?.tenantId && loginUserInfo?.companyId) {
      setSearchParamState(loginUserInfo?.tenantId, loginUserInfo?.companyId, 'Top', {
        centerIds: requestParam.centerIds,
        orgSupplierIds: requestParam.orgSupplierIds,
        sort: requestParam.sort,
        pageNo: requestParam.pageNo,
        limit: requestParam.limit,
      })
    }
  }

  // ソート順とページ番号の変更通知
  const onListStatusChange = (status: DataListStatus) => {
    const trPageNo = status.paginationModel.page + 1

    fetchTopAlertData({
      ...searchParam,
      limit: status.paginationModel.pageSize,
      pageNo: trPageNo,
    }).catch(() => undefined)

    if (loginUserInfo?.tenantId && loginUserInfo?.companyId) {
      setSearchParamState(loginUserInfo?.tenantId, loginUserInfo?.companyId, 'Top', {
        centerIds: searchParam.centerIds,
        orgSupplierIds: searchParam.orgSupplierIds,
        sort: searchParam.sort,
        limit: status.paginationModel.pageSize,
        pageNo: trPageNo,
      })
    }

    setListStatus({ ...listStatus, sortModel: status.sortModel, paginationModel: status.paginationModel })
  }

  useEffect(() => {
    if (!loginUserInfo?.companyId) return

    fetchInfomessage({ companyId: loginUserInfo.companyId, limit: 30, sort: ['info_time,desc'] })
  }, [loginUserInfo?.companyId])

  useEffect(() => {
    if (!loginUserInfo?.companyId || hasLoadedInitialOrderAlert.current) return

    if (localCenterIds.length > 0 || localOrgSupplierIds.length > 0) {
      fetchTopAlertData({ ...searchParam, sort: localSort }).catch(() => undefined)
      setListStatus((prev) => ({
        ...prev,
        paginationModel: {
          pageSize: initSearchParam.limit ?? 100,
          page: initSearchParam.pageNo ?? 0,
        },
      }))
    }

    hasLoadedInitialOrderAlert.current = true
  }, [
    loginUserInfo?.companyId,
    localCenterIds,
    localOrgSupplierIds,
    searchParam,
    localSort,
    initSearchParam.limit,
    initSearchParam.pageNo,
    fetchTopAlertData,
  ])

  return (
    <Box>
      <Box sx={{ marginTop: '1.5rem' }}>
        <Typography variant="mediumBold">お知らせ</Typography>
        <Box border={1}>
          {/* 企業全体のお知らせ情報を30件取得し縦スクロールで5件ずつ表示する */}
          <InfoMessageDataList list={infomessage} isLoading={infoMessageIsLoading} />
        </Box>
      </Box>

      <Box sx={{ marginTop: '2rem' }}>
        <Typography variant="mediumBold">発注アラート</Typography>
        <SearchCondition searchParam={initSearchParam} onSearch={onSearch} result={result} />
        <Box width="100%">
          <OrderAlertDataList
            list={orderAlert}
            isLoading={orderAlertIsLoading}
            listStatus={listStatus}
            onListStatusChange={onListStatusChange}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default Top
